import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SettingsIcon from '@mui/icons-material/Settings';
import TodayIcon from '@mui/icons-material/Today';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Snackbar,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config/api';

const monthNames = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
];

const SHORT = {
    regular: 'Ф',
    swap: 'З',
    extra_shift: 'ДС',
    sold_shift: 'ПР',
    extra_excursion: '+1',
    extra_2_excursions: '+2',
};

const pad = (n) => String(n).padStart(2, '0');
const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const dateKey = (v) => (v ? String(v).slice(0, 10) : '');
const timeShort = (v) => (v ? String(v).slice(0, 5) : '');
const fmtMoney = (v) => (Number(v) || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
const cellW = 52;
const nameW = 188;

function isGuide(emp) {
    return (emp.position || '').toLowerCase().includes('гид');
}
function isAdmin(emp) {
    const p = (emp.position || '').toLowerCase();
    return p.includes('админ') || p.includes('менедж') || p.includes('владел');
}

export default function EmployeeCalendarPanel({ user, token }) {
    const today = new Date();
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth());
    const [tab, setTab] = useState('guides');
    const [employees, setEmployees] = useState([]);
    const [shiftTypes, setShiftTypes] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [adminShifts, setAdminShifts] = useState([]);
    const [hints, setHints] = useState([]);
    const [rates, setRates] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState('');
    const [guideDialog, setGuideDialog] = useState(null);
    const [adminDialog, setAdminDialog] = useState(null);
    const [ratesOpen, setRatesOpen] = useState(false);
    const [ratesDraft, setRatesDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    const canEdit = ['owner', 'admin', 'manager'].includes(user?.role);
    const canManageRates = user?.role === 'owner';
    const visibleShiftTypes = shiftTypes.filter((t) => t.code !== 'swap' || String(t.id) === String(guideDialog?.shift_type_id));
    const headers = useMemo(() => ({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
    }), [token]);

    const dateFrom = useMemo(() => isoDate(new Date(year, month, 1)), [year, month]);
    const dateTo = useMemo(() => isoDate(new Date(year, month + 1, 0)), [year, month]);
    const daysInMonth = useMemo(() => new Date(year, month + 1, 0).getDate(), [year, month]);
    const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);

    const loadAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [empR, stR, wsR, asR, hintR, ratesR] = await Promise.all([
                fetch(`${API_URL}/api/employees?status=active`, { headers }),
                fetch(`${API_URL}/api/shift-types`, { headers }),
                fetch(`${API_URL}/api/work-sessions?date_from=${dateFrom}&date_to=${dateTo}`, { headers }),
                fetch(`${API_URL}/api/admin-shifts?date_from=${dateFrom}&date_to=${dateTo}`, { headers }),
                fetch(`${API_URL}/api/work-sessions/slot-hints?date_from=${dateFrom}&date_to=${dateTo}`, { headers }),
                fetch(`${API_URL}/api/payroll/rates`, { headers }),
            ]);
            const empData = await empR.json();
            const stData = await stR.json();
            const wsData = await wsR.json();
            const asData = await asR.json();
            const hintData = await hintR.json();
            const ratesData = await ratesR.json();
            setEmployees(empData.employees || empData.data || []);
            setShiftTypes(stData.data || []);
            setSessions(wsData.data || []);
            setAdminShifts(asData.data || []);
            setHints(hintData.data || []);
            if (ratesData.success) setRates(ratesData.data);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [headers, dateFrom, dateTo]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const guides = useMemo(() => employees.filter(isGuide).sort((a, b) => a.full_name.localeCompare(b.full_name, 'ru')), [employees]);
    const admins = useMemo(() => {
        const list = employees.filter(isAdmin).sort((a, b) => a.full_name.localeCompare(b.full_name, 'ru'));
        return list.length ? list : employees.filter((e) => !isGuide(e));
    }, [employees]);
    const rows = tab === 'guides' ? guides : admins;

    const sessionIndex = useMemo(() => {
        const idx = {};
        sessions.forEach((s) => { idx[`${s.employee_id}_${dateKey(s.work_date)}`] = s; });
        return idx;
    }, [sessions]);

    const adminIndex = useMemo(() => {
        const idx = {};
        adminShifts.forEach((s) => { idx[`${s.employee_id}_${dateKey(s.shift_date)}`] = s; });
        return idx;
    }, [adminShifts]);

    const hintIndex = useMemo(() => {
        const idx = {};
        hints.forEach((h) => { idx[`${h.employee_id}_${dateKey(h.work_date)}`] = h; });
        return idx;
    }, [hints]);

    const isWeekend = (day) => {
        const w = new Date(year, month, day).getDay();
        return w === 0 || w === 6;
    };
    const isToday = (day) => {
        const t = new Date();
        return t.getFullYear() === year && t.getMonth() === month && t.getDate() === day;
    };
    const weekdayShort = (day) => ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'][new Date(year, month, day).getDay()];

    const openGuideCell = (employee, day) => {
        if (!canEdit) return;
        const date = isoDate(new Date(year, month, day));
        const session = sessionIndex[`${employee.id}_${date}`];
        const hint = hintIndex[`${employee.id}_${date}`];
        const defaultType = shiftTypes.find((t) => t.code === 'regular') || shiftTypes[0];
        setGuideDialog({
            employee,
            date,
            day,
            id: session?.id || null,
            shift_type_id: session?.shift_type_id || defaultType?.id || '',
            actual_start: timeShort(session?.actual_start) || hint?.first_start || '',
            actual_end: timeShort(session?.actual_end) || hint?.last_end || '',
            excursion_count: session?.excursion_count ?? hint?.excursion_count ?? '',
            swap_with_employee_id: session?.swap_with_employee_id || null,
            notes: session?.notes || '',
            hint,
        });
    };

    const openAdminCell = (employee, day) => {
        if (!canEdit) return;
        const date = isoDate(new Date(year, month, day));
        const shift = adminIndex[`${employee.id}_${date}`];
        setAdminDialog({
            employee,
            date,
            day,
            id: shift?.id || null,
            shift_kind: shift?.shift_kind || 'main',
            notes: shift?.notes || '',
        });
    };

    const saveGuide = async () => {
        if (!guideDialog?.shift_type_id) {
            setError('Выберите тип смены');
            return;
        }
        const type = shiftTypes.find((t) => String(t.id) === String(guideDialog.shift_type_id));
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/api/work-sessions`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    employee_id: guideDialog.employee.id,
                    work_date: guideDialog.date,
                    shift_type_id: Number(guideDialog.shift_type_id),
                    actual_start: guideDialog.actual_start || null,
                    actual_end: guideDialog.actual_end || null,
                    excursion_count: parseInt(guideDialog.excursion_count, 10) || (type?.code === 'extra_2_excursions' ? 2 : type?.code === 'extra_excursion' ? 1 : 0),
                    swap_with_employee_id: (type?.code === 'swap' || type?.code === 'sold_shift') ? guideDialog.swap_with_employee_id : null,
                    notes: guideDialog.notes || (type?.code === 'swap' && guideDialog.swap_with_employee_id
                        ? `Замена с ${(guides.find((g) => g.id === guideDialog.swap_with_employee_id) || {}).full_name || ''}`
                        : null),
                }),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Не удалось сохранить');
            setToast(`Смена сохранена${data.data?.calculated_bonus > 0 ? ` · +${fmtMoney(data.data.calculated_bonus)} ₽` : ''}`);
            setGuideDialog(null);
            await loadAll();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const deleteGuide = async () => {
        if (!guideDialog?.id) {
            setGuideDialog(null);
            return;
        }
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/api/work-sessions/${guideDialog.id}`, { method: 'DELETE', headers });
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Не удалось удалить');
            setToast('Смена снята');
            setGuideDialog(null);
            await loadAll();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const saveAdmin = async () => {
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/api/admin-shifts`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    shift_date: adminDialog.date,
                    employee_id: adminDialog.employee.id,
                    shift_kind: adminDialog.shift_kind,
                    notes: adminDialog.notes || null,
                }),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Не удалось сохранить');
            setToast('Смена администратора сохранена');
            setAdminDialog(null);
            await loadAll();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const deleteAdmin = async () => {
        if (!adminDialog?.id) {
            setAdminDialog(null);
            return;
        }
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/api/admin-shifts/${adminDialog.id}`, { method: 'DELETE', headers });
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Не удалось удалить');
            setToast('Смена снята');
            setAdminDialog(null);
            await loadAll();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const openRates = () => {
        if (!rates) return;
        setRatesDraft({
            shift_types: (rates.shift_types || []).map((t) => ({ ...t })),
            bonus_rules: (rates.bonus_rules || []).map((r) => ({ ...r })),
            admin_shift_pay: rates.admin_shift_pay,
            guide_shift_pay: rates.guide_shift_pay || 0,
            cafe_percent: rates.cafe_percent,
            souvenirs_percent: rates.souvenirs_percent,
        });
        setRatesOpen(true);
    };

    const saveRates = async () => {
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/api/payroll/rates`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(ratesDraft),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Не удалось сохранить ставки');
            await fetch(`${API_URL}/api/work-sessions/recalculate`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ date_from: dateFrom, date_to: dateTo }),
            });
            setToast('Ставки сохранены, доплаты за месяц пересчитаны');
            setRatesOpen(false);
            await loadAll();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const renderGuideCell = (employee, day) => {
        const date = isoDate(new Date(year, month, day));
        const session = sessionIndex[`${employee.id}_${date}`];
        const hint = hintIndex[`${employee.id}_${date}`];
        const weekend = isWeekend(day);
        const todayCell = isToday(day);
        const code = session?.shift_type_code;
        const bg = session ? (session.shift_type_color || '#1976d2') : (weekend ? '#fafafa' : '#fff');
        const label = session ? (SHORT[code] || '•') : (hint ? '·' : '');
        const times = session && (session.actual_start || session.actual_end)
            ? `${timeShort(session.actual_start)}–${timeShort(session.actual_end)}`
            : '';

        const tip = session
            ? `${session.shift_type_name || 'Смена'}${times ? ` · ${times}` : ''}${session.swap_with_name ? ` · с ${session.swap_with_name}` : ''}${Number(session.calculated_bonus) > 0 ? ` · +${fmtMoney(session.calculated_bonus)} ₽` : ''}`
            : (hint ? `Слоты: ${hint.excursion_count} экс. ${hint.first_start}–${hint.last_end}. Нажмите, чтобы поставить смену` : 'Нажмите, чтобы поставить смену');

        return (
            <Tooltip key={day} title={tip} arrow>
                <Box
                    onClick={() => openGuideCell(employee, day)}
                    sx={{
                        width: cellW,
                        minHeight: 44,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid',
                        borderColor: todayCell ? '#1976d2' : '#eee',
                        background: bg,
                        color: session ? '#fff' : (weekend ? '#bdbdbd' : '#9e9e9e'),
                        cursor: canEdit ? 'pointer' : 'default',
                        fontSize: 11,
                        fontWeight: 700,
                        lineHeight: 1.15,
                        '&:hover': canEdit ? { outline: '2px solid #1976d2', zIndex: 1 } : {},
                    }}
                >
                    <span>{label}</span>
                    {times && <span style={{ fontSize: 8, fontWeight: 500, opacity: 0.9 }}>{times}</span>}
                </Box>
            </Tooltip>
        );
    };

    const renderAdminCell = (employee, day) => {
        const date = isoDate(new Date(year, month, day));
        const shift = adminIndex[`${employee.id}_${date}`];
        const weekend = isWeekend(day);
        const todayCell = isToday(day);
        const kind = shift?.shift_kind || 'main';
        const bg = !shift ? (weekend ? '#fafafa' : '#fff') : (kind === 'bar' ? '#6d4c41' : '#0277bd');
        return (
            <Tooltip key={day} title={shift ? (kind === 'bar' ? 'Барная смена' : 'Основная смена') : 'Нажмите, чтобы поставить смену'} arrow>
                <Box
                    onClick={() => openAdminCell(employee, day)}
                    sx={{
                        width: cellW,
                        minHeight: 44,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid',
                        borderColor: todayCell ? '#1976d2' : '#eee',
                        background: bg,
                        color: shift ? '#fff' : (weekend ? '#bdbdbd' : '#9e9e9e'),
                        cursor: canEdit ? 'pointer' : 'default',
                        fontSize: 11,
                        fontWeight: 700,
                        '&:hover': canEdit ? { outline: '2px solid #1976d2', zIndex: 1 } : {},
                    }}
                >
                    {shift ? (kind === 'bar' ? 'Б' : 'О') : ''}
                </Box>
            </Tooltip>
        );
    };

    const totalsFor = (employee) => {
        if (tab === 'guides') {
            const list = sessions.filter((s) => s.employee_id === employee.id);
            return {
                shifts: list.length,
                hours: list.reduce((sum, s) => sum + (parseFloat(s.total_hours) || 0), 0),
                bonus: list.reduce((sum, s) => sum + (parseFloat(s.calculated_bonus) || 0), 0),
            };
        }
        const list = adminShifts.filter((s) => s.employee_id === employee.id);
        const pay = (rates?.admin_shift_pay || 3000) * list.length;
        return { shifts: list.length, hours: 0, bonus: pay };
    };

    const selectedType = shiftTypes.find((t) => String(t.id) === String(guideDialog?.shift_type_id));

    return (
        <Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>Календарь смен</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Клик по клетке — поставить или изменить смену. Ставки доплат правятся здесь же.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    {canManageRates && (
                        <Button startIcon={<SettingsIcon />} variant="outlined" onClick={openRates} disabled={!rates}>
                            Ставки
                        </Button>
                    )}
                    <Button onClick={loadAll} variant="outlined" disabled={loading}>Обновить</Button>
                </Stack>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Paper sx={{ p: 1.5, mb: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} justifyContent="space-between">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <IconButton onClick={() => { if (month === 0) { setMonth(11); setYear((y) => y - 1); } else setMonth((m) => m - 1); }}><ChevronLeftIcon /></IconButton>
                        <Typography variant="h6" sx={{ minWidth: 190, textAlign: 'center', fontWeight: 600 }}>
                            {monthNames[month]} {year}
                        </Typography>
                        <IconButton onClick={() => { if (month === 11) { setMonth(0); setYear((y) => y + 1); } else setMonth((m) => m + 1); }}><ChevronRightIcon /></IconButton>
                        <Button startIcon={<TodayIcon />} size="small" onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth()); }}>Сегодня</Button>
                    </Stack>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 36 }}>
                        <Tab value="guides" label={`Гиды (${guides.length})`} />
                        <Tab value="admins" label={`Админы (${admins.length})`} />
                    </Tabs>
                </Stack>
            </Paper>

            {tab === 'guides' ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                    {shiftTypes.filter((t) => t.code !== 'swap').map((t) => (
                        <Chip
                            key={t.id}
                            size="small"
                            label={`${SHORT[t.code] || t.name}: ${t.name}${Number(t.fixed_bonus) > 0 ? ` · ${fmtMoney(t.fixed_bonus)} ₽` : ''}`}
                            sx={{ bgcolor: t.color_code, color: '#fff', fontWeight: 600 }}
                        />
                    ))}
                </Stack>
            ) : (
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                    <Chip size="small" label="О — основная" sx={{ bgcolor: '#0277bd', color: '#fff' }} />
                    <Chip size="small" label="Б — барная" sx={{ bgcolor: '#6d4c41', color: '#fff' }} />
                    <Chip size="small" label={`Смена = ${fmtMoney(rates?.admin_shift_pay || 3000)} ₽`} variant="outlined" />
                </Stack>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
                <Paper sx={{ overflowX: 'auto' }}>
                    <Box sx={{ display: 'inline-block', minWidth: '100%' }}>
                        <Box sx={{ display: 'flex', position: 'sticky', top: 0, zIndex: 2, bgcolor: '#fff' }}>
                            <Box sx={{ width: nameW, flexShrink: 0, p: 1, fontWeight: 700, fontSize: 13, borderBottom: '2px solid #1976d2' }}>
                                Сотрудник
                            </Box>
                            {days.map((d) => (
                                <Box key={d} sx={{
                                    width: cellW, textAlign: 'center', fontSize: 10, py: 0.5,
                                    borderBottom: '2px solid #1976d2',
                                    background: isWeekend(d) ? '#fafafa' : '#fff',
                                    color: isToday(d) ? '#1976d2' : (isWeekend(d) ? '#9e9e9e' : '#333'),
                                    fontWeight: isToday(d) ? 700 : 500,
                                }}>
                                    <div>{weekdayShort(d)}</div>
                                    <div style={{ fontSize: 12 }}>{d}</div>
                                </Box>
                            ))}
                            <Box sx={{ width: 160, flexShrink: 0, p: 1, fontWeight: 700, fontSize: 12, borderBottom: '2px solid #1976d2', borderLeft: '2px solid #1976d2' }}>
                                Итог
                            </Box>
                        </Box>

                        {rows.length === 0 && (
                            <Alert severity="info" sx={{ m: 2 }}>Нет сотрудников в этой вкладке</Alert>
                        )}
                        {rows.map((emp) => {
                            const t = totalsFor(emp);
                            return (
                                <Box key={emp.id} sx={{ display: 'flex', '&:hover': { bgcolor: '#fafafa' } }}>
                                    <Box sx={{ width: nameW, flexShrink: 0, px: 1, py: 0.5, borderBottom: '1px solid #eee', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{emp.full_name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{emp.position}</Typography>
                                    </Box>
                                    {days.map((d) => tab === 'guides' ? renderGuideCell(emp, d) : renderAdminCell(emp, d))}
                                    <Box sx={{ width: 160, flexShrink: 0, px: 1, py: 0.5, borderBottom: '1px solid #eee', borderLeft: '2px solid #eee', fontSize: 11, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.3 }}>
                                        <span>{t.shifts} см.{tab === 'guides' && t.hours ? ` · ${t.hours.toFixed(1)} ч` : ''}</span>
                                        {t.bonus > 0 && <span style={{ color: '#2e7d32', fontWeight: 700 }}>+{fmtMoney(t.bonus)} ₽</span>}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Paper>
            )}

            <Dialog open={!!guideDialog} onClose={() => setGuideDialog(null)} fullWidth maxWidth="sm">
                <DialogTitle>
                    {guideDialog?.employee?.full_name}
                    <Typography variant="body2" color="text.secondary">
                        {guideDialog?.day}.{pad(month + 1)}.{year}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {guideDialog?.hint && (
                            <Alert severity="info">
                                По слотам дня: {guideDialog.hint.excursion_count} экскурсий, {guideDialog.hint.first_start}–{guideDialog.hint.last_end}
                            </Alert>
                        )}
                        <FormControl fullWidth>
                            <InputLabel>Тип выхода</InputLabel>
                            <Select
                                label="Тип выхода"
                                value={guideDialog?.shift_type_id || ''}
                                onChange={(e) => setGuideDialog((d) => ({ ...d, shift_type_id: e.target.value }))}
                            >
                                {visibleShiftTypes.map((t) => (
                                    <MenuItem key={t.id} value={t.id}>
                                        {t.name}{Number(t.fixed_bonus) > 0 ? ` · +${fmtMoney(t.fixed_bonus)} ₽` : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Приход"
                                type="time"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={guideDialog?.actual_start || ''}
                                onChange={(e) => setGuideDialog((d) => ({ ...d, actual_start: e.target.value }))}
                            />
                            <TextField
                                label="Уход"
                                type="time"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={guideDialog?.actual_end || ''}
                                onChange={(e) => setGuideDialog((d) => ({ ...d, actual_end: e.target.value }))}
                            />
                        </Stack>
                        <TextField
                            label="Экскурсий за смену"
                            type="number"
                            fullWidth
                            value={guideDialog?.excursion_count ?? ''}
                            onChange={(e) => setGuideDialog((d) => ({ ...d, excursion_count: e.target.value }))}
                            helperText="Норма 4. Если в посещениях за день больше 4 слотов — 5-я считается сама"
                        />
                        {selectedType?.code === 'swap' && (
                            <Autocomplete
                                options={guides.filter((g) => g.id !== guideDialog.employee.id)}
                                getOptionLabel={(o) => o.full_name}
                                value={guides.find((g) => g.id === guideDialog.swap_with_employee_id) || null}
                                onChange={(_, v) => setGuideDialog((d) => ({ ...d, swap_with_employee_id: v?.id || null }))}
                                renderInput={(params) => <TextField {...params} label="С кем замена" />}
                            />
                        )}
                        {selectedType?.code === 'sold_shift' && (
                            <Autocomplete
                                options={guides.filter((g) => g.id !== guideDialog.employee.id)}
                                getOptionLabel={(o) => o.full_name}
                                value={guides.find((g) => g.id === guideDialog.swap_with_employee_id) || null}
                                onChange={(_, v) => setGuideDialog((d) => ({ ...d, swap_with_employee_id: v?.id || null }))}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Кто продал смену"
                                        helperText="Покупатель уже получил 2500 на руки. В зарплате 2500 вернутся продавцу."
                                    />
                                )}
                            />
                        )}
                        <TextField
                            label="Комментарий"
                            fullWidth
                            multiline
                            minRows={2}
                            value={guideDialog?.notes || ''}
                            onChange={(e) => setGuideDialog((d) => ({ ...d, notes: e.target.value }))}
                            placeholder={selectedType?.code === 'swap' ? 'Например: Иванова–Петров 19–20' : ''}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    {guideDialog?.id && <Button color="error" onClick={deleteGuide} disabled={saving}>Снять смену</Button>}
                    <Box sx={{ flex: 1 }} />
                    <Button onClick={() => setGuideDialog(null)}>Отмена</Button>
                    <Button variant="contained" onClick={saveGuide} disabled={saving}>Сохранить</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!adminDialog} onClose={() => setAdminDialog(null)} fullWidth maxWidth="xs">
                <DialogTitle>
                    {adminDialog?.employee?.full_name}
                    <Typography variant="body2" color="text.secondary">
                        {adminDialog?.day}.{pad(month + 1)}.{year}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <RadioGroup
                        value={adminDialog?.shift_kind || 'main'}
                        onChange={(e) => setAdminDialog((d) => ({ ...d, shift_kind: e.target.value }))}
                    >
                        <FormControlLabel value="main" control={<Radio />} label="Основная смена" />
                        <FormControlLabel value="bar" control={<Radio />} label="Барная смена" />
                    </RadioGroup>
                    <TextField
                        label="Комментарий"
                        fullWidth
                        sx={{ mt: 1 }}
                        value={adminDialog?.notes || ''}
                        onChange={(e) => setAdminDialog((d) => ({ ...d, notes: e.target.value }))}
                    />
                </DialogContent>
                <DialogActions>
                    {adminDialog?.id && <Button color="error" onClick={deleteAdmin} disabled={saving}>Снять</Button>}
                    <Box sx={{ flex: 1 }} />
                    <Button onClick={() => setAdminDialog(null)}>Отмена</Button>
                    <Button variant="contained" onClick={saveAdmin} disabled={saving}>Сохранить</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={ratesOpen} onClose={() => setRatesOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Ставки доплат</DialogTitle>
                <DialogContent>
                    {ratesDraft && (
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Typography variant="subtitle2">Оплата за выход</Typography>
                            <TextField
                                label="Ставка за выход гида (полная смена)"
                                type="number"
                                value={ratesDraft.guide_shift_pay}
                                onChange={(e) => setRatesDraft((d) => ({ ...d, guide_shift_pay: e.target.value }))}
                                helperText="Факт, замена и доп. смена. Продажа: 2500 возвращаются продавцу, покупателю ставка не платится."
                            />
                            <Divider />
                            <Typography variant="subtitle2">Доплаты за тип смены (сверх ставки)</Typography>
                            {ratesDraft.shift_types.map((t, i) => (
                                <TextField
                                    key={t.id}
                                    label={t.name}
                                    type="number"
                                    value={t.fixed_bonus}
                                    onChange={(e) => setRatesDraft((d) => {
                                        const next = { ...d, shift_types: d.shift_types.map((x, idx) => idx === i ? { ...x, fixed_bonus: e.target.value } : x) };
                                        return next;
                                    })}
                                    helperText={['regular', 'swap', 'sold_shift'].includes(t.code) ? 'Обычно 0 — выход уже оплачен ставкой выше' : undefined}
                                    InputProps={{ endAdornment: <Typography sx={{ ml: 1 }}>₽</Typography> }}
                                />
                            ))}
                            <Divider />
                            <Typography variant="subtitle2">Переработки и 5-я экскурсия</Typography>
                            {ratesDraft.bonus_rules.map((r, i) => (
                                <TextField
                                    key={r.id}
                                    label={r.name}
                                    type="number"
                                    value={r.bonus_amount}
                                    onChange={(e) => setRatesDraft((d) => ({
                                        ...d,
                                        bonus_rules: d.bonus_rules.map((x, idx) => idx === i ? { ...x, bonus_amount: e.target.value } : x),
                                    }))}
                                    InputProps={{ endAdornment: <Typography sx={{ ml: 1 }}>₽</Typography> }}
                                />
                            ))}
                            <Divider />
                            <Typography variant="subtitle2">Админы и проценты</Typography>
                            <TextField
                                label="Оплата смены администратора"
                                type="number"
                                value={ratesDraft.admin_shift_pay}
                                onChange={(e) => setRatesDraft((d) => ({ ...d, admin_shift_pay: e.target.value }))}
                            />
                            <TextField
                                label="% кафе гидам"
                                type="number"
                                value={ratesDraft.cafe_percent}
                                onChange={(e) => setRatesDraft((d) => ({ ...d, cafe_percent: e.target.value }))}
                            />
                            <TextField
                                label="% сувенирки админам"
                                type="number"
                                value={ratesDraft.souvenirs_percent}
                                onChange={(e) => setRatesDraft((d) => ({ ...d, souvenirs_percent: e.target.value }))}
                            />
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRatesOpen(false)}>Отмена</Button>
                    <Button variant="contained" onClick={saveRates} disabled={saving}>Сохранить ставки</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />
        </Box>
    );
}
